import { BehaviorEngineConfig } from '@/behavior';
import type { GomarkePixelConfig } from '../types';
import { parseConfigFromScriptTag } from './parseConfigFromScriptTag';

/** Resposta da API de configuração do pixel (getSetting). */
export type SettingsResponse = {
  title?: string;
  sendLead?: boolean;
  sendLeadText?: string;
  sendLeadDetection?: string;
  addToCart?: boolean;
  addToCartText?: string;
  addToCartDetection?: string;
  initiateCheckout?: boolean;
  initiateCheckoutDetection?: string;
  initiateCheckoutDetectionText?: string;
  purchaseSendType?: string;
  purchaseValueType?: string;
  type?: string;
  productIds?: string[];
  sendIp?: string;
  pixelMeta?: Array<{ title?: string; externalId?: string; token?: string }>;
  eventsGoogle?: string[];
  behavior?: BehaviorEngineConfig;
};

export const getSetting = async ({
  id,
  apiKey,
  endpoint,
}: {
  id: string;
  apiKey?: string;
  endpoint: string;
}): Promise<SettingsResponse> => {
  const response = await fetch(`${endpoint}/pixels/public/${id}`, {
    headers: apiKey
      ? {
          Authorization: `Bearer ${apiKey}`,
        }
      : {},
  });
  const data = await response.json();
  return data;
};

/**
 * Obtém a config do pixel sempre via getSetting.
 * Usa o script tag apenas para id, endpoint e apiKey da chamada.
 * Em caso de falha (rede/teste), retorna config default para testes.
 */
export async function getConfigFromScriptTag(
  scriptTag: HTMLScriptElement
): Promise<GomarkePixelConfig> {
  const baseSettings = parseConfigFromScriptTag(scriptTag);
  const settingsBootstrap: GomarkePixelConfig = {
    apiEndpoint: baseSettings.apiEndpoint,
    pixel: baseSettings.pixel,
    debug: baseSettings.debug,
    timeout: baseSettings.timeout,
    projectId: baseSettings.projectId,
    pageId: baseSettings.pageId,
    behavior: {
      enabled: true,
      heartbeatOnly: true,
      presenceHeartbeatEnabled: true,
      heartbeatIntervalMs: 30000,
      hiddenHeartbeatMinIntervalMs: 30000,
      viewport: {
        enabled: false,
        selector: 'data-viewport-key',
        selectorType: 'attribute' as const,
        trackElementVisibility: false,
        trackElementIntersection: false,
        visibilityThreshold: 0.5,
        trackElementClicks: false,
        trackElementHovers: false,
        maxTrackingElements: 10,
        intersectionThreshold: 0.5,
      },
      engagementThresholds: {
        low: 0.5,
        medium: 0.7,
      },
      scrollWeights: {
        scroll: 1,
        click: 1,
        time: 1,
      },
      idleThresholdMs: 1000,
      rageClickWindowMs: 1000,
      rageClickThreshold: 10,
    },
    autoTracks: {
      lead: {
        enabled: true,
        selector: 'data-lead',
        selectorType: 'attribute',
      },
      addToCart: {
        enabled: true,
        selector: 'data-add-to-cart',
        selectorType: 'attribute',
        productDataSelectors: {
          id: 'data-product-id',
          name: 'data-product-name',
          price: 'data-product-price',
          category: 'data-product-category',
          image: 'data-product-image',
          currency: 'data-product-currency',
        },
      },
      removeToCart: {
        enabled: true,
        selector: 'data-remove-to-cart',
        selectorType: 'attribute',
        productDataSelectors: {
          id: 'data-product-id',
          name: 'data-product-name',
          price: 'data-product-price',
          category: 'data-product-category',
          image: 'data-product-image',
        },
      },
      initiateCheckout: {
        enabled: true,
        selector: 'data-initiate-checkout',
        selectorType: 'attribute',
        cartDataSelectors: {
          total: 'data-cart-total',
          currency: 'data-cart-currency',
          items: 'data-cart-items',
        },
      },
      addPaymentInfo: {
        enabled: true,
        paymentMethod: 'CREDIT_CARD',
        detectCardFields: true,
        selector: 'data-checkout',
        selectorType: 'attribute',
        cartDataSelectors: {
          total: 'data-cart-total',
          currency: 'data-cart-currency',
          items: 'data-cart-items',
        },
      },
      customer: {
        enabled: true,
        autoAddToCart: true,
        debounceMs: 300,
      },
      scroll: {
        enabled: false,
        scrollThresholds: [25, 50, 75, 90],
        trackScrollDepth: true,
        trackTimeOnPage: true,
        debounceMs: 300,
      },
      viewContent: {
        enabled: false,
        selector: 'data-view-content',
        selectorType: 'attribute',
      },
    },
    // autoTrack: {
    //   selectors: [
    //     { selector: '[data-autotrack-selector]', eventType: 'purchase' },
    //   ],
    //   texts: [{ text: /finalizar compra/i, eventType: 'initiateCheckout' }],
    //   urlPatterns: [{ pattern: 'obrigado', eventType: 'purchase' }],
    // },
  };

  try {
    const remoteSettings = await getSetting({
      id: settingsBootstrap.pixel,
      endpoint: settingsBootstrap.apiEndpoint,
      apiKey: settingsBootstrap.apiKey,
    });

    return {
      ...settingsBootstrap,
      pixelMeta: remoteSettings.pixelMeta || [],
    };
  } catch {
    return settingsBootstrap;
  }
}
